// import AlertsContext from 'common/contexts/alerts';
import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { UsersIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import { User } from "types";

import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";
import SiteSelect from "components/select/SiteSelect/SiteSelect";
import WorkSiteIcon from "components/Icon/icons/WorkSiteIcon";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
// import AlertsContext from "common/providers/alerts";
import useOrganization from "common/hooks/useOrganization";
import getWording from "common/utils/wording";
import AlertsContext from "common/providers/alerts";
import Select from "common/components/Select/Select";
import { PostType } from "types/PostType";
import { Constants } from "../../constants/Constants";
import UserPublicItem from "./UserPublicItem";

function UsersPublic() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const { organization } = useOrganization();
  const canManageUsers = currentUser?.isAdmin || currentUser?.isSuperadmin;

  const baseFilters = {
    confirmed: true,
  };

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<User>("users", {
    defaultFilters: organization?.id
      ? {
          ...baseFilters,
          "organizations][id": organization?.id,
          "role][name": "Authenticated",
        }
      : { ...baseFilters, "role][name": "Authenticated" },
    defaultSort: "name:ASC",
    populate: [
      "avatar",
      "siteUsers",
      "siteUsers.site",
      "siteUsers.site.mainImage",
      "siteUsers.site.logo",
      "siteUsers.jobPosition",
      "followedSites.mainImage",
      "followedSites.logo",
      "organizations",
      "contacts",
      "contacts.contactGroups",
    ],
    cachePrefix: `UsersPublic_${organization?.id || "all"}`,
  });

  // une seule requête ici
  const { items: postTypes } = useList<PostType>("post-types", {
    defaultSort: "name:ASC",
    cachePrefix: "PostTypes",
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  useEffect(() => {
    setFilter("organizations][id", organization?.id || false);
  }, [organization, setFilter]);

  const { removeItem } = useItem<User>("users", "");

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e: any) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/public-accounts/edit/${id}`);
  };

  const styleTop = "md:w-1/3 xl:flex-1 xl:w-auto";
  const styleBottom = "md:w-1/3 mt-2 xl:flex-1 xl:w-auto xl:mt-0";

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$contains",
        columnClasses: styleTop,
      },
      {
        Header: t("forms.email"),
        filterName: "email$contains",
        columnClasses: styleTop,
      },
      {
        Header: t("forms.phoneNumber"),
        filterName: "phoneNumber$contains",
        columnClasses: styleBottom,
      },
      {
        Header: t("forms.site", {
          wording: getWording(false, true),
        }),
        hidden: !organization,
        filterName: "siteUsers][site][id",
        filterComponent: (props: any) => (
          <SiteSelect
            isFilter
            allowReadOnlyOptions
            Icon={WorkSiteIcon}
            {...props}
          />
        ),
        columnClasses: styleBottom,
      },
      {
        Header: t("forms.followedSites"),
        hidden: organization,
        filterName: "followedSites][id",
        filterComponent: (props: any) => (
          <SiteSelect
            isFilter
            allowReadOnlyOptions
            Icon={WorkSiteIcon}
            {...props}
          />
        ),
        columnClasses: styleBottom,
      },
      {
        Header: t("forms.authentication"),
        hidden: !Constants.ENABLE_LOGIN,
        filterName: "provider",
        filterComponent: (props: any) => (
          <Select
            isFilter
            placeholder={t("forms.authentication")}
            options={[
              { value: "local", label: "Local" },
              {
                value: Constants.LOGIN_PROVIDER,
                label:
                  Constants.LOGIN_PROVIDER.charAt(0).toUpperCase() +
                  Constants.LOGIN_PROVIDER.slice(1),
              },
            ]}
            {...props}
          />
        ),
        columnClasses: styleBottom,
      },
      {
        Header: t("forms.confirmed"),
        filterName: "confirmed",
        filterComponent: (props: any) => (
          <div className="pt-2">
            <ToggleSwitch rightLabel={t("forms.confirmed")} {...props} />
          </div>
        ),
        // headerClasses: "mb-3",
        columnClasses: styleBottom,
      },
    ],
    [t, organization]
  ) as any;

  const renderItem = useCallback(
    (props: { item: User; [key: string]: any }) => {
      // Tous les postTypes (avec leur valeur ou false par défaut)
      const mappedSettings = Object.fromEntries(
        postTypes?.map((pt: PostType) => {
          const settingName = `${pt.id}`;
          const settingState = props.item.notificationSettings?.[settingName];
          return [pt.name, settingState === "true" || settingState === true];
        }) || []
      );

      const notificationSettings: Record<string, boolean> = {
        ...mappedSettings,
        sms:
          props.item.notificationSettings?.sms === "true" ||
          props.item.notificationSettings?.sms === true ||
          false,
      };

      return (
        <UserPublicItem
          {...props}
          item={{
            ...props.item,
            notificationSettings,
          }}
        />
      );
    },
    [postTypes]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.usersPublic")}
        subtitle={t(
          Constants.IS_LOGIN_MANDATORY_APP
            ? "titleLegends.followerUsers"
            : "titleLegends.users"
        )}
        btnTitle={t("newItem.user")}
        btnSubtitle={t("newItem.userLegend")}
        Icon={UsersIcon}
        // addLink="/public-accounts/add"
        // addLinkDisabled={!canManageUsers || !organization?.id}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={renderItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default UsersPublic;
